import React, { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Info } from "lucide-react";

const SEGMENTS = [
  {
    id: "social",
    name: "Social",
    tagline: "F2 · F3",
    from: 8_000_000,
    to: 15_000_000,
    img: "https://images.pexels.com/photos/8146330/pexels-photo-8146330.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    features: ["Accès CDMH & FSH", "42% du programme", "Finitions standards", "Charges maîtrisées"],
  },
  {
    id: "eco",
    name: "Économique",
    tagline: "F3 · F4",
    from: 15_000_000,
    to: 30_000_000,
    img: "https://images.pexels.com/photos/7587828/pexels-photo-7587828.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    features: ["Crédit classique 25 ans", "Balcon inclus", "Domotique light", "Parking privatif"],
  },
  {
    id: "standing",
    name: "Standing",
    tagline: "F3 · F5",
    from: 30_000_000,
    to: 65_000_000,
    img: "https://images.unsplash.com/photo-1666282167632-c613fbeb163c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODMyNzE4MjV8MA&ixlib=rb-4.1.0&q=85",
    features: ["Cuisine équipée", "Terrasse", "Pack EKIPTECH smart", "Concierge quartier"],
  },
  {
    id: "premium",
    name: "Premium",
    tagline: "F4 · F6",
    from: 65_000_000,
    to: 200_000_000,
    img: "https://images.unsplash.com/photo-1628592102751-ba83b0314276?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODMyNzE4MjV8MA&ixlib=rb-4.1.0&q=85",
    features: ["Vue signature", "Domotique premium", "Golf & marina Sassandra", "Services 24/7"],
  },
];

const formatFCFA = (n) => `${Math.round(n).toLocaleString("fr-FR").replace(/,/g, " ")} FCFA`;
const formatShort = (n) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M` : n.toLocaleString("fr-FR");

// Monthly payment: standard amortization
const monthly = (principal, annualRate, years) => {
  if (principal <= 0 || years <= 0) return 0;
  const r = annualRate / 12;
  const n = years * 12;
  if (r === 0) return principal / n;
  return (principal * r) / (1 - Math.pow(1 + r, -n));
};

const Configurator = () => {
  const [segId, setSegId] = useState("standing");
  const seg = SEGMENTS.find((s) => s.id === segId);
  const midPrice = Math.round((seg.from + seg.to) / 2);

  const [price, setPrice] = useState(midPrice);
  const [apport, setApport] = useState(Math.round(midPrice * 0.2));
  const [duration, setDuration] = useState(20);
  const rate = 0.08; // 8% indicative

  React.useEffect(() => {
    const mid = Math.round((seg.from + seg.to) / 2);
    setPrice(mid);
    setApport(Math.round(mid * 0.2));
  }, [segId]); // eslint-disable-line

  const financed = Math.max(0, price - apport);
  const monthlyPayment = useMemo(() => monthly(financed, rate, duration), [financed, duration]);
  const totalCost = monthlyPayment * duration * 12;
  const totalInterest = totalCost - financed;

  const pieData = [
    { name: "Apport", value: apport },
    { name: "Crédit", value: financed },
  ];

  return (
    <section
      id="configurateur"
      data-testid="configurator-section"
      className="relative bg-[#FAFAF8] py-24 md:py-40 border-t border-[#0C2340]/8"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-16">
        <div className="flex items-center gap-3 mb-8">
          <span className="kidh-diamond" />
          <span className="font-body text-[0.7rem] uppercase tracking-[0.32em] text-[#636E72]">
            Votre projet
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16">
          <h2
            data-testid="configurator-title"
            className="lg:col-span-8 font-heading font-black text-[#0C2340] text-4xl md:text-6xl leading-[0.95] tracking-tighter"
          >
            Configurez.<br />
            <span className="text-[#E8611A]">Simulez.</span> Concrétisez.
          </h2>
          <p className="lg:col-span-4 font-body text-[#2D3436] text-base md:text-lg leading-relaxed">
            Quatre segments, un simulateur temps réel. Ajustez le prix, votre
            apport et la durée pour projeter votre acquisition.
          </p>
        </div>

        {/* Segment selector */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5 mb-12">
          {SEGMENTS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSegId(s.id)}
              data-testid={`segment-${s.id}`}
              className={`group text-left relative overflow-hidden aspect-[4/5] transition-all duration-500 ${
                segId === s.id ? "ring-2 ring-[#E8611A]" : "ring-1 ring-[#0C2340]/10"
              }`}
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 group-hover:scale-110"
                style={{ backgroundImage: `url(${s.img})` }}
              />
              <div
                className={`absolute inset-0 transition-opacity duration-500 ${
                  segId === s.id
                    ? "bg-gradient-to-t from-[#0C2340]/95 via-[#0C2340]/60 to-[#0C2340]/10"
                    : "bg-gradient-to-t from-[#0C2340]/85 via-[#0C2340]/40 to-transparent"
                }`}
              />
              <div className="relative h-full flex flex-col justify-between p-5 md:p-6 text-white">
                <div className="flex items-center justify-between">
                  <span
                    className="w-2 h-2 rotate-45"
                    style={{ background: segId === s.id ? "#E8611A" : "#FAFAF8" }}
                  />
                  <span className="font-body text-[0.55rem] uppercase tracking-[0.24em] opacity-70">
                    {s.tagline}
                  </span>
                </div>
                <div>
                  <div className="font-heading font-black text-2xl md:text-3xl tracking-tighter leading-none mb-1">
                    {s.name}
                  </div>
                  <div className="font-body text-[0.65rem] uppercase tracking-[0.22em] opacity-80">
                    dès {formatShort(s.from)} FCFA
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Simulator */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Sliders */}
          <div className="lg:col-span-7 space-y-10">
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="font-body text-[0.7rem] uppercase tracking-[0.24em] text-[#636E72]">
                  Prix indicatif · segment {seg.name}
                </label>
                <span className="font-heading font-black text-[#0C2340] text-xl md:text-2xl tracking-tight" data-testid="price-value">
                  {formatFCFA(price)}
                </span>
              </div>
              <Slider
                value={[price]}
                min={seg.from}
                max={seg.to}
                step={100_000}
                onValueChange={([v]) => {
                  setPrice(v);
                  if (apport > v * 0.9) setApport(Math.round(v * 0.2));
                }}
                data-testid="price-slider"
              />
              <div className="flex justify-between mt-2 font-body text-[0.65rem] uppercase tracking-[0.22em] text-[#636E72]">
                <span>{formatShort(seg.from)} FCFA</span>
                <span>{formatShort(seg.to)} FCFA</span>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="font-body text-[0.7rem] uppercase tracking-[0.24em] text-[#636E72]">
                  Apport personnel
                </label>
                <span className="font-heading font-black text-[#0C2340] text-xl md:text-2xl tracking-tight" data-testid="apport-value">
                  {formatFCFA(apport)} · {Math.round((apport / price) * 100)}%
                </span>
              </div>
              <Slider
                value={[apport]}
                min={0}
                max={Math.round(price * 0.9)}
                step={100_000}
                onValueChange={([v]) => setApport(v)}
                data-testid="apport-slider"
              />
              <div className="flex justify-between mt-2 font-body text-[0.65rem] uppercase tracking-[0.22em] text-[#636E72]">
                <span>0 FCFA</span>
                <span>{formatShort(price * 0.9)} FCFA</span>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="font-body text-[0.7rem] uppercase tracking-[0.24em] text-[#636E72]">
                  Durée du crédit
                </label>
                <span className="font-heading font-black text-[#0C2340] text-xl md:text-2xl tracking-tight" data-testid="duration-value">
                  {duration} ans
                </span>
              </div>
              <Slider
                value={[duration]}
                min={5}
                max={25}
                step={1}
                onValueChange={([v]) => setDuration(v)}
                data-testid="duration-slider"
              />
              <div className="flex justify-between mt-2 font-body text-[0.65rem] uppercase tracking-[0.22em] text-[#636E72]">
                <span>5 ans</span>
                <span>25 ans</span>
              </div>
            </div>

            {/* Features */}
            <div className="grid grid-cols-2 gap-3 mt-8 pt-8 border-t border-[#0C2340]/10">
              {seg.features.map((f) => (
                <div key={f} className="flex items-center gap-3">
                  <span className="kidh-diamond" />
                  <span className="font-body text-[#2D3436] text-sm">{f}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Results panel */}
          <div className="lg:col-span-5 bg-[#0A1C33] text-white p-8 md:p-10 relative" data-testid="simulator-results">
            <div className="font-body text-[0.65rem] uppercase tracking-[0.28em] text-white/60 mb-3">
              Mensualité estimée
            </div>
            <div className="font-heading font-black text-white text-5xl md:text-6xl tracking-tighter leading-none mb-2" data-testid="monthly-value">
              {formatFCFA(monthlyPayment)}
            </div>
            <div className="font-body text-white/50 text-xs">
              Taux indicatif 8% · Assurance non incluse
            </div>

            {/* Donut */}
            <div className="h-52 my-8 -mx-4">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    innerRadius={55}
                    outerRadius={85}
                    startAngle={90}
                    endAngle={-270}
                    dataKey="value"
                    stroke="none"
                  >
                    <Cell fill="#E8611A" />
                    <Cell fill="#FAFAF8" />
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#0C2340", border: "none", color: "#FAFAF8", fontSize: 12 }}
                    formatter={(v) => formatFCFA(v)}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-2 gap-6 border-t border-white/12 pt-6">
              <div>
                <div className="font-body text-white/60 text-[0.6rem] uppercase tracking-[0.24em]">
                  Montant financé
                </div>
                <div className="font-heading font-black text-white text-lg md:text-xl tracking-tight mt-1" data-testid="financed-value">
                  {formatFCFA(financed)}
                </div>
              </div>
              <div>
                <div className="font-body text-white/60 text-[0.6rem] uppercase tracking-[0.24em]">
                  Coût total du crédit
                </div>
                <div className="font-heading font-black text-white text-lg md:text-xl tracking-tight mt-1" data-testid="total-cost-value">
                  {formatFCFA(totalCost)}
                </div>
              </div>
              <div>
                <div className="font-body text-white/60 text-[0.6rem] uppercase tracking-[0.24em]">
                  Intérêts cumulés
                </div>
                <div className="font-heading font-black text-white text-lg md:text-xl tracking-tight mt-1">
                  {formatFCFA(totalInterest)}
                </div>
              </div>
              <div>
                <div className="font-body text-white/60 text-[0.6rem] uppercase tracking-[0.24em]">
                  Ratio apport
                </div>
                <div className="font-heading font-black text-[#E8611A] text-lg md:text-xl tracking-tight mt-1">
                  {Math.round((apport / price) * 100)}%
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-start gap-3 text-white/50">
              <Info size={14} className="mt-0.5 shrink-0" />
              <p className="font-body text-[0.65rem] leading-relaxed">
                Simulation indicative. Conditions définitives déterminées par l'établissement bancaire partenaire (SIB, SGBCI, BSIC, BICICI).
              </p>
            </div>

            <a
              href="#contact"
              className="mt-8 kidh-btn-primary w-full justify-center"
              data-testid="simulator-cta"
            >
              Être rappelé par un conseiller
              <span className="kidh-diamond" style={{ background: "#FAFAF8" }} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Configurator;
